import React, { lazy, startTransition, useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import pages from '../nav.json';
import Layout from '../components/Layout';
import Toast from '../components/molecules/Toast';
import { AuthContext } from '../Context/authContext';
import { preloadViews } from './prefetch';
import { NAV_ITEMS } from '../components/atoms/TopBar/navItems';
import { viewSkeleton } from '../components/atoms/skeleton/viewSkeletons';

/** Fallback for an unknown or invalid view — the app's home, same as RouteGuard. */
const HOME_ROUTE = '/ai-home';

/**
 * One lazy component per page file, made once and reused.
 *
 * A fresh lazy() on every visit suspended every time, even with the code
 * already downloaded, so the route skeleton flashed for a moment before each
 * page (an empty page included). Reusing the same component, React renders it
 * at once after its first load, and a re-run of the loader (it also runs when
 * the user refreshes) keeps the page mounted instead of remounting it.
 */
const viewCache = new Map();

const importView = file => {
  if (!viewCache.has(file)) {
    viewCache.set(
      file,
      lazy(() =>
        import(`./${file}`).catch(() => {
          viewCache.delete(file);
          Toast({
            type: 'error',
            message: `Error in importing ${file}`,
          });
        }),
      ),
    );
  }
  return viewCache.get(file);
};

export default function Index() {
  const { loading_user, workspaceReady } = useContextHook(AuthContext, ['loading_user', 'workspaceReady']);
  const history = useNavigate();
  const location = useLocation();

  const { view } = useParams();
  /**
   * The views a URL is still allowed to reach.
   *
   * nav.json is the whole catalogue, including screens nothing in the app links
   * to any more. An entry flagged `"parked": true` keeps every field it had —
   * JSON cannot hold a comment, so that flag is how one is written — and is
   * dropped here, which sends its path through the same redirect an unknown view
   * already gets. Remove the flag to bring a screen back.
   */
  const metaViewData = pages.filter(page => !page.parked);
  const [selectedView, setSelectedView] = useState([]);

  // After the first page has rendered, fetch the other sidebar pages' code.
  const firstViewShown = selectedView.length > 0;
  useEffect(() => {
    if (!firstViewShown) return;
    // The top bar's pages, in its order, so the ones nearest the user come first.
    preloadViews(NAV_ITEMS.map(item => item.path).filter(file => file !== view));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstViewShown]);

  async function loadView(filtered) {
    // Add validation to ensure filtered array contains valid objects
    if (!filtered || filtered.length === 0 || !filtered[0]) {
      Toast({
        type: 'error',
        message: 'Invalid route or page not found',
      });
      // Redirect to dashboard as fallback
      history(HOME_ROUTE, { replace: true });
      return;
    }

    const promise = filtered.map(async _view => {
      if (!_view || !_view.file) {
        Toast({
          type: 'error',
          message: 'Invalid view configuration',
        });
        return null;
      }

      const View = await importView(_view.file);

      // eslint-disable-next-line no-use-before-define
      return <View key={_view.id || _view.file} selectView={selectView} />;
    });
    const validPromises = promise.filter(p => p !== null);
    if (validPromises.length > 0) {
      // As a transition, so the page on screen stays up while the next one's
      // code loads instead of blanking in between.
      Promise.all(validPromises).then(views => startTransition(() => setSelectedView(views)));
    }
  }

  async function selectView(file) {
    if (!file) {
      // Toast({
      //   type: 'error',
      //   message: 'No file specified',
      // });
      history(HOME_ROUTE, { replace: true });
      return;
    }

    const filtered = metaViewData.filter(elem => elem.file === file);

    if (filtered.length === 0) {
      // Toast({
      //   type: 'error',
      //   message: `Page "${file}" not found`,
      // });
      history(HOME_ROUTE, { replace: true });
      return;
    }

    loadView([filtered[0]]);
  }

  useEffect(() => {
    let fileToLoad = view;

    // Validate that the view exists in navigation data
    const viewExists = metaViewData.some(elem => elem.file === fileToLoad);

    if (!viewExists) {
      // Toast({
      //   type: 'error',
      //   message: `Page "${fileToLoad}" not found. Redirecting to dashboard.`,
      // });
      history(HOME_ROUTE, { replace: true });
      return;
    }

    const targetPath = `/${fileToLoad}`;
    if (location.pathname !== targetPath) {
      history({ pathname: targetPath, search: location.search }, { replace: true });
    }
    selectView(fileToLoad);
  }, [view, loading_user]);

  return (
    <Layout
      merged={view === 'dashboard'}
      title={view}
      showTemplate={metaViewData?.filter(elem => elem.file === view)[0]?.navigations}
      topBar>
      {/* The page's own skeleton, never a blank screen, until the page can render:
          while the workspace resolves (a request sent before then would go to
          the token's workspace instead of the selected one), before the first
          page is picked, and while its code downloads. The page then shows the
          same skeleton itself until its data is in. */}
      <Suspense fallback={viewSkeleton(view)}>
        {workspaceReady && selectedView.length ? selectedView : viewSkeleton(view)}
      </Suspense>
    </Layout>
  );
}
